import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "../../../ui/button";

export const BulkScoreModal = ({ games, onSave, onClose }) => {
  const [scores, setScores] = useState(
    games.map(g => ({ gameId: g.id, homeScore: "", awayScore: "", game: g }))
  );
  const [saving, setSaving] = useState(false);

  const updateScore = (idx, field, value) => {
    const newScores = [...scores];
    newScores[idx][field] = value;
    setScores(newScores);
  };

  const handleSave = async () => {
    const validScores = scores.filter(s => s.homeScore !== "" && s.awayScore !== "");
    if (validScores.length === 0) return;
    
    setSaving(true);
    await onSave(validScores.map(s => ({
      gameId: s.gameId,
      homeScore: parseInt(s.homeScore),
      awayScore: parseInt(s.awayScore)
    })));
    setSaving(false);
  };

  const filledCount = scores.filter(s => s.homeScore !== "" && s.awayScore !== "").length;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Enter Game Scores</h3>
            <p className="text-xs text-zinc-500">{games.length} games need scores</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 uppercase">
                <th className="pb-2">Date</th>
                <th className="pb-2">Away</th>
                <th className="pb-2 text-center">Score</th>
                <th className="pb-2">Home</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((s, idx) => (
                <tr key={s.gameId} className="border-t border-zinc-800">
                  <td className="py-2 text-zinc-400 text-xs">
                    {new Date(s.game.scheduled_date + "T12:00:00").toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                  <td className="py-2">
                    <span className="text-white text-xs">{s.game.away_team?.substring(0, 12)}</span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center justify-center gap-1">
                      <input
                        type="number"
                        value={s.awayScore}
                        onChange={(e) => updateScore(idx, 'awayScore', e.target.value)}
                        className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-xs text-center"
                        placeholder="0"
                        min="0"
                      />
                      <span className="text-zinc-500">-</span>
                      <input
                        type="number"
                        value={s.homeScore}
                        onChange={(e) => updateScore(idx, 'homeScore', e.target.value)}
                        className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-xs text-center"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </td>
                  <td className="py-2">
                    <span className="text-white text-xs">{s.game.home_team?.substring(0, 12)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
          <span className="text-xs text-zinc-500">{filledCount} of {games.length} scores entered</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleSave}
              disabled={filledCount === 0 || saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save {filledCount} Score{filledCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
